import type { NodeEntry } from 'slate';
import { v4 as uuid4 } from 'uuid';

import type { InputFieldElement } from '../../../../slate-api';
import { lucidaSansTypewriterFamily, lucidaSansTypewriteType } from '../../../../slate-api';
import type { DocumentContext, DOCXInline } from '../internal';
import { createTextRun } from '../internal';

export function createSignatureField(
  signatureFieldEntry: NodeEntry<InputFieldElement>,
  options: { context: DocumentContext }
): DOCXInline[] {
  const { context } = options;
  /**
   * The anchor run is only meaningful to DocuSign. Without it the field contributes nothing
   * to the DOCX, since its own child text is always empty.
   */
  if (!context.includeDocuSignUuidTags) return [];

  const [signatureField] = signatureFieldEntry;
  const { anchorString, inlines } = getUniqueAnchorStringTextRun(signatureFieldEntry, options);

  context.addSignature({ anchorString, fieldId: signatureField.fieldId });

  return inlines;
}

interface AnchorStringNode {
  anchorString: string;
  inlines: DOCXInline[];
}

/* Create a text node with anchor string in white letters at width 225px and returns map with anchor string and node. */
function getUniqueAnchorStringTextRun(
  signatureFieldEntry: NodeEntry<InputFieldElement>,
  options: { context: DocumentContext }
): AnchorStringNode {
  const uuid = uuid4();
  const anchorString = `\\${uuid}\\`;
  const [, signatureFieldPath] = signatureFieldEntry;

  /* 9px font size, Lucida Sans Typewriter Normal - 256px for UUID in two slashes \${uuid}\*/
  return {
    anchorString,
    inlines: createTextRun(
      [
        {
          bold: {
            active: false,
            origin: 'INHERITED',
          },
          comments: [],
          fontColor: {
            color: '#FFFFFF',
            origin: 'INHERITED',
          },
          fontSize: { origin: 'INHERITED', size: 8.5 },
          fontType: {
            fontFamily: lucidaSansTypewriterFamily,
            origin: 'INHERITED',
            type: lucidaSansTypewriteType,
          },
          highlight: {
            color: null,
            origin: 'INHERITED',
          },
          italic: {
            active: false,
            origin: 'INHERITED',
          },
          suggestedEdits: [],
          text: anchorString,
          underline: {
            active: false,
            origin: 'INHERITED',
          },
        },
        signatureFieldPath.concat(0),
      ],
      options
    ),
  };
}
